import { useQuery } from "@tanstack/react-query";

/**
 * Public content, fetched once and shared by every section. Same-origin in
 * both dev (CRA proxy) and production (nginx), so no base URL to configure.
 */
export const useContent = () =>
  useQuery({
    queryKey: ["content"],
    queryFn: () => fetch("/api/content").then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    }),
    staleTime: 5 * 60_000,
  });

/**
 * Localised field lookup. When the visitor's language is missing, English is
 * the better fallback for a foreign hunter than Romanian; Romanian is always
 * present (the estate fills it in), so nothing ever renders empty.
 */
export const pick = (localized, lang) =>
  localized?.[lang] ?? localized?.en ?? localized?.ro ?? "";

const MONTHS = {
  ro: ["ian", "feb", "mar", "apr", "mai", "iun", "iul", "aug", "sep", "oct", "noi", "dec"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  de: ["Jan", "Feb", "März", "Apr", "Mai", "Juni", "Juli", "Aug", "Sept", "Okt", "Nov", "Dez"],
};

/** Number/date locale per site language. */
const LOCALES = { ro: "ro-RO", en: "en-GB", de: "de-DE" };
export const localeFor = (lang) => LOCALES[lang] || LOCALES.ro;

/** "09-01" -> "1 sep" (ro) / "1 Sep" (en). */
export const formatMonthDay = (md, lang) => {
  const [m, d] = String(md).split("-");
  const months = MONTHS[lang] || MONTHS.ro;
  const name = months[Number(m) - 1] ?? m;
  return `${Number(d)} ${name}`;
};

/** Season list or the year-round marker, rendered as one readable string. */
export const formatSeasons = (seasons, lang, t) => {
  if (!seasons) return null;
  if (!Array.isArray(seasons)) return seasons.yearRound ? t.pricing.yearRound : null;
  if (seasons.length === 0) return null;
  return seasons
    .map((s) => {
      const range = `${formatMonthDay(s.from, lang)} – ${formatMonthDay(s.to, lang)}`;
      const note = pick(s.note, lang);
      return note ? `${range} (${note})` : range;
    })
    .join(" · ");
};

/** Prices are whole euros throughout; grouping keeps 4-digit sums readable. */
export const eur = (n, lang) => `${Number(n).toLocaleString(localeFor(lang))} €`;

/**
 * The headline number for a species card. Everything on the public side leads
 * with "from X" because that is the question visitors actually arrive with.
 */
export const startingPrice = (species) => {
  const p = species.pricing;
  if (!p) return null;
  if (p.kind === "flat") return p.price;
  if (p.kind === "trophy_grid") {
    return species.package?.basePrice ?? null;
  }
  if (p.kind === "group_tiers" || p.kind === "bag_tiers") {
    const prices = p.rows.map((r) => r.price).filter((v) => typeof v === "number");
    return prices.length ? Math.min(...prices) : null;
  }
  return null;
};
